import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Trash2, 
  ToggleLeft, 
  ToggleRight, 
  Shield, 
  Key,
  Database,
  User,
  Save,
  X
} from 'lucide-react';

const ParametresAvances = ({ commercant, onUpdate, onDelete, loading = false }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [slug, setSlug] = useState(commercant?.slug || '');
  const [slugChanged, setSlugChanged] = useState(false);
  const [advancedData, setAdvancedData] = useState({
    actif: commercant?.actif ?? true,
    modeMaintenance: commercant?.modeMaintenance ?? false,
    commission: commercant?.commission ?? 0,
    limiteCommandes: commercant?.limiteCommandes ?? null,
    notes: commercant?.notes ?? '',
    // Gestion des stocks
    stock_management_enabled: commercant?.stock_management_enabled ?? false,
    stock_display_threshold: commercant?.stock_display_threshold ?? null,
    stock_alert_threshold: commercant?.stock_alert_threshold ?? 5,
    stock_reservation_enabled: commercant?.stock_reservation_enabled ?? false,
    stock_reservation_duration: commercant?.stock_reservation_duration ?? 15,
    // Paramètres de commande
    joursReservationAvance: commercant?.joursReservationAvance ?? 0
  });

  const handleToggleActif = () => {
    const newData = { ...advancedData, actif: !advancedData.actif };
    setAdvancedData(newData);
    onUpdate(newData);
  };

  const handleToggleMaintenance = () => {
    const newData = { ...advancedData, modeMaintenance: !advancedData.modeMaintenance };
    setAdvancedData(newData);
    onUpdate(newData);
  };

  const handleInputChange = (field, value) => {
    setAdvancedData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveAdvanced = () => {
    onUpdate(advancedData);
    setShowAdvancedSettings(false);
  };

  const handleDelete = () => {
    onDelete(commercant.id);
    setShowDeleteConfirm(false);
  };

  // Synchroniser le slug quand le commerçant change
  useEffect(() => {
    if (commercant?.slug !== undefined) {
      setSlug(commercant.slug || '');
      setSlugChanged(false);
    }
  }, [commercant?.slug]);

  return (
    <div className="space-y-6">
      {/* Header avec avertissement */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Zone Super Admin</h3>
            <p className="text-sm text-red-700 mt-1">
              Ces paramètres sont réservés aux super administrateurs. 
              Les modifications peuvent impacter le fonctionnement de la boutique.
            </p>
          </div>
        </div>
      </div>

      {/* Statut de la boutique */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2" />
          Statut de la boutique
        </h4>
        
        <div className="space-y-4">
          {/* Activation/Désactivation */}
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900">Boutique active</h5>
              <p className="text-sm text-gray-500">
                {advancedData.actif 
                  ? 'La boutique est visible et accessible aux clients'
                  : 'La boutique est masquée et inaccessible aux clients'
                }
              </p>
            </div>
            <button
              onClick={handleToggleActif}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              {advancedData.actif ? (
                <ToggleRight className="h-8 w-8 text-green-600" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-gray-400" />
              )}
            </button>
          </div>

          {/* Mode maintenance */}
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-medium text-gray-900">Mode maintenance</h5>
              <p className="text-sm text-gray-500">
                {advancedData.modeMaintenance 
                  ? 'La boutique affiche une page de maintenance'
                  : 'La boutique fonctionne normalement'
                }
              </p>
            </div>
            <button
              onClick={handleToggleMaintenance}
              disabled={loading}
              className="flex items-center space-x-2"
            >
              {advancedData.modeMaintenance ? (
                <ToggleRight className="h-8 w-8 text-orange-600" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 📦 Gestion Intelligente des Stocks */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2 text-blue-600" />
          Gestion Intelligente des Stocks
        </h4>
        
        <div className="space-y-6">
          {/* Activation gestion stocks */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="flex-1">
              <h5 className="font-medium text-gray-900">Activer la gestion des stocks</h5>
              <p className="text-sm text-gray-500 mt-1">
                Si désactivé, tous les produits seront en stock illimité. 
                Recommandé pour restaurants (préparations à la demande).
              </p>
            </div>
            <button
              onClick={() => {
                const newData = { 
                  ...advancedData, 
                  stock_management_enabled: !advancedData.stock_management_enabled 
                };
                setAdvancedData(newData);
                onUpdate(newData);
              }}
              disabled={loading}
              className="flex items-center space-x-2 ml-4"
            >
              {advancedData.stock_management_enabled ? (
                <ToggleRight className="h-8 w-8 text-blue-600" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-gray-400" />
              )}
            </button>
          </div>

          {/* Paramètres détaillés (si gestion activée) */}
          {advancedData.stock_management_enabled && (
            <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
              {/* Seuil d'affichage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seuil d'affichage "Plus que X en stock"
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Le message s'affichera aux clients quand le stock est ≤ ce seuil. 
                  Laisser vide pour ne jamais afficher.
                </p>
                <input
                  type="number"
                  min="0"
                  value={advancedData.stock_display_threshold || ''}
                  onChange={(e) => handleInputChange('stock_display_threshold', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 10 (afficher si ≤ 10 unités)"
                />
              </div>

              {/* Seuil d'alerte admin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seuil d'alerte admin (stock bas)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Vous recevrez une alerte quand le stock est ≤ ce seuil.
                </p>
                <input
                  type="number"
                  min="0"
                  value={advancedData.stock_alert_threshold || ''}
                  onChange={(e) => handleInputChange('stock_alert_threshold', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: 5"
                />
              </div>

              {/* Réservations temporaires */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">Réservations temporaires (panier)</h5>
                  <p className="text-sm text-gray-500 mt-1">
                    Réserve le stock quand un client ajoute au panier. 
                    Évite la survente. Recommandé pour produits à stock limité.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const newData = { 
                      ...advancedData, 
                      stock_reservation_enabled: !advancedData.stock_reservation_enabled 
                    };
                    setAdvancedData(newData);
                    onUpdate(newData);
                  }}
                  disabled={loading}
                  className="flex items-center space-x-2 ml-4"
                >
                  {advancedData.stock_reservation_enabled ? (
                    <ToggleRight className="h-8 w-8 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-8 w-8 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Durée réservation */}
              {advancedData.stock_reservation_enabled && (
                <div className="pl-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée de réservation (minutes)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Le stock sera libéré automatiquement si le client n'a pas validé sa commande dans ce délai.
                  </p>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    value={advancedData.stock_reservation_duration}
                    onChange={(e) => handleInputChange('stock_reservation_duration', parseInt(e.target.value) || 15)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="15"
                  />
                </div>
              )}

              {/* Exemple de configuration */}
              <div className="bg-white p-4 rounded border border-blue-200 mt-4">
                <h6 className="font-medium text-gray-800 mb-2 text-sm">📋 Aperçu configuration</h6>
                <div className="space-y-1 text-xs text-gray-600">
                  {advancedData.stock_display_threshold !== null && (
                    <p>• Message "Plus que X" affiché si stock ≤ {advancedData.stock_display_threshold}</p>
                  )}
                  {!advancedData.stock_display_threshold && (
                    <p>• Message "Plus que X" jamais affiché aux clients</p>
                  )}
                  <p>• Alerte admin si stock ≤ {advancedData.stock_alert_threshold || 5}</p>
                  {advancedData.stock_reservation_enabled ? (
                    <p>• Réservations actives ({advancedData.stock_reservation_duration} min)</p>
                  ) : (
                    <p>• Pas de réservations (risque de survente)</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 📅 Paramètres de Commande */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Database className="h-5 w-5 mr-2 text-blue-600" />
          Paramètres de Commande
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Réservation à l&apos;avance (horizon)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Nombre de jours maximum dans le futur où vos clients peuvent réserver (commandes, rendez-vous, créneaux).
              <strong className="text-blue-600"> Ex. 60 = jusqu&apos;à 2 mois, 90 = 3 mois.</strong>
            </p>
            <input
              type="number"
              min="7"
              max="365"
              value={advancedData.joursReservationAvance}
              onChange={(e) => handleInputChange('joursReservationAvance', parseInt(e.target.value, 10) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="60"
            />
            <div className="mt-2 text-xs text-gray-600">
              {advancedData.joursReservationAvance >= 7 ? (
                <p className="text-green-600 font-medium">
                  Les clients peuvent réserver jusqu&apos;à {advancedData.joursReservationAvance} jour(s) à l&apos;avance.
                </p>
              ) : (
                <p className="text-orange-600">Indiquez au minimum 7 jours (recommandé : 30 à 90 pour les RDV).</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6">
        <div className="space-y-4">
          
          {/* Configurations type par secteur */}
          {advancedData.stock_management_enabled && (
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <h6 className="font-medium text-gray-800 mb-3 text-sm">💡 Configurations recommandées</h6>
              <div className="space-y-3">
                {/* Restaurant */}
                <div className="bg-white p-3 rounded border border-gray-200">
                  <h6 className="font-semibold text-xs text-gray-700 mb-1">🍕 Restaurant</h6>
                  <p className="text-xs text-gray-600">Désactiver la gestion (stock illimité)</p>
                </div>
                
                {/* Piscine / Magasin */}
                <div className="bg-white p-3 rounded border border-gray-200">
                  <h6 className="font-semibold text-xs text-gray-700 mb-1">🏊 Piscine / Magasin</h6>
                  <p className="text-xs text-gray-600">
                    Activer • Seuil affichage: 10 • Seuil alerte: 5 • Réservations: OUI (15 min)
                  </p>
                </div>
                
                {/* Plat du jour */}
                <div className="bg-white p-3 rounded border border-gray-200">
                  <h6 className="font-semibold text-xs text-gray-700 mb-1">🍱 Plat du jour</h6>
                  <p className="text-xs text-gray-600">
                    Activer • Seuil affichage: vide • Seuil alerte: 5 • Réservations: NON
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Paramètres avancés */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center">
            <Key className="h-5 w-5 mr-2" />
            Paramètres avancés
          </h4>
          <button
            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            {showAdvancedSettings ? 'Masquer' : 'Afficher'}
          </button>
        </div>

        {showAdvancedSettings && (
          <div className="space-y-4">
            {/* Commission */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commission (%) 
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={advancedData.commission}
                onChange={(e) => handleInputChange('commission', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.0"
              />
            </div>

            {/* Limite de commandes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Limite de commandes par jour
              </label>
              <input
                type="number"
                min="0"
                value={advancedData.limiteCommandes || ''}
                onChange={(e) => handleInputChange('limiteCommandes', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Aucune limite"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes internes
              </label>
              <textarea
                value={advancedData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Notes privées sur cette boutique..."
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleSaveAdvanced}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center space-x-2"
              >
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </button>
              <button
                onClick={() => setShowAdvancedSettings(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Annuler</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Informations système */}
      <div className="bg-white border rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Informations système
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ID Boutique
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={commercant?.id || ''}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
              <button
                onClick={() => navigator.clipboard.writeText(commercant?.id || '')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                title="Copier l'ID"
              >
                <Key className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Slug (URL publique)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={slug}
                onChange={(e) => {
                  // Normaliser le slug : minuscules, seulement lettres, chiffres et tirets
                  let newSlug = e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '') // Supprimer caractères spéciaux
                    .replace(/-+/g, '-') // Remplacer plusieurs tirets par un seul
                    .replace(/^-|-$/g, '') // Supprimer tirets en début/fin
                    .trim();
                  
                  // Limiter la longueur
                  if (newSlug.length > 50) {
                    newSlug = newSlug.substring(0, 50);
                  }
                  
                  setSlug(newSlug);
                  setSlugChanged(newSlug !== (commercant?.slug || ''));
                }}
                placeholder="sushilover"
                maxLength={50}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  slugChanged 
                    ? 'border-blue-500 focus:ring-blue-500 bg-blue-50' 
                    : 'border-gray-300 focus:ring-blue-500 bg-white'
                }`}
              />
              {slugChanged && (
                <button
                  onClick={() => {
                    onUpdate({ slug });
                    setSlugChanged(false);
                  }}
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                >
                  <Save className="h-4 w-4" />
                  <span>Sauvegarder</span>
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              URL publique: <span className="font-mono">/{slug || commercant?.slug || 'slug'}</span>
            </p>
            {slugChanged && (
              <p className="mt-1 text-xs text-blue-600">
                ⚠️ Modification non sauvegardée. Cliquez sur "Sauvegarder" pour appliquer.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Créé le
            </label>
            <input
              type="text"
              value={commercant?.createdAt ? new Date(commercant.createdAt).toLocaleString('fr-FR') : ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Modifié le
            </label>
            <input
              type="text"
              value={commercant?.updatedAt ? new Date(commercant.updatedAt).toLocaleString('fr-FR') : ''}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
            />
          </div>
        </div>
      </div>

      {/* Actions dangereuses */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-red-900 mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          Actions dangereuses
        </h4>
        
        <div className="space-y-4">
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-medium text-red-900">Supprimer la boutique</h5>
                <p className="text-sm text-red-700 mt-1">
                  Cette action est irréversible. Toutes les données seront perdues.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmer la suppression
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer définitivement la boutique 
              <strong> "{commercant?.nom}"</strong> ?
              <br /><br />
              Cette action supprimera :
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Tous les produits</li>
                <li>Toutes les commandes</li>
                <li>Toutes les données clients</li>
                <li>Tous les paramètres</li>
              </ul>
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 flex-1"
              >
                {loading ? 'Suppression...' : 'Supprimer définitivement'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex-1"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametresAvances;
